import React, { useState, useEffect } from 'react';
import { 
    Plus, Syringe, MapPin, Search, Pencil, Trash2, X, AlertTriangle, UserCheck, 
    SquarePlus, UserMinus, ShieldQuestion, Check 
} from 'lucide-react';

// Constantes para la Persistencia Local
const LOCAL_STORAGE_KEY_OFFICES = 'cleth_offices_data';
const LOCAL_STORAGE_KEY_DOCTORS = 'cleth_doctors_data';

// --- CONSTANTES DE ESTADOS Y MOCK DATA ---

// Lista de posibles estados para un consultorio
const AVAILABLE_STATUSES = ['Activo', 'En Mantenimiento', 'Inactivo'];

// Mock data inicial de médicos. Esto se cargará o reemplazará por localStorage.
const INITIAL_DOCTORS = ['Dr. Héctor Silva', 'Dra. Ana Gómez', 'Dr. Carlos Ruiz', 'Dra. Sofía Díaz', 'Dra. Valeria Pérez'];

// Mock data de consultorios registrados inicialmente
const INITIAL_OFFICES = [
    { id: 1, nombreConsultorio: 'Consulta General - Piso 1', piso: '1', medicoResponsable: 'Dr. Héctor Silva', estado: 'Activo' },
    { id: 2, nombreConsultorio: 'Pediatría - Ala B', piso: '2', medicoResponsable: 'Dra. Ana Gómez', estado: 'Activo' },
    { id: 3, nombreConsultorio: 'Sala de Cirugía Menor', piso: '3', medicoResponsable: 'Dr. Carlos Ruiz', estado: 'En Mantenimiento' },
];

const DEFAULT_FORM_DATA = {
    id: null,
    nombreConsultorio: '',
    piso: '1',
    medicoResponsable: '',
    estado: 'Activo', 
};


const Consultorios = () => {
    // 1. Inicialización de estados con localStorage
    const [offices, setOffices] = useState(() => {
        const localData = localStorage.getItem(LOCAL_STORAGE_KEY_OFFICES);
        return localData ? JSON.parse(localData) : INITIAL_OFFICES;
    });

    const [availableDoctors, setAvailableDoctors] = useState(() => {
        const localData = localStorage.getItem(LOCAL_STORAGE_KEY_DOCTORS);
        return localData ? JSON.parse(localData) : INITIAL_DOCTORS;
    });

    // Estados para la funcionalidad de BÚSQUEDA
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para el Modal de Crear/Editar Consultorio
    const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
    const [isEditing, setIsEditing] = useState(false);

    // Estados para el Modal de Gestión de Doctores
    const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
    const [newDoctorName, setNewDoctorName] = useState('');

    // Estados para el Modal de CONFIRMACIÓN de Eliminación
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [officeToDelete, setOfficeToDelete] = useState(null);

    // 2. Efecto para GUARDAR los datos en localStorage cada vez que 'offices' o 'availableDoctors' cambian
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY_OFFICES, JSON.stringify(offices));
    }, [offices]);
    
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY_DOCTORS, JSON.stringify(availableDoctors));
    }, [availableDoctors]);


    // **********************************************
    // LÓGICA DE GESTIÓN DE DOCTORES
    // **********************************************

    const handleAddDoctor = (e) => {
        e.preventDefault();
        const trimmedName = newDoctorName.trim();
        if (trimmedName && !availableDoctors.includes(trimmedName)) {
            setAvailableDoctors(prev => [...prev, trimmedName]);
            setNewDoctorName('');
        }
    };
    
    const handleDeleteDoctor = (doctorName) => {
        // 1. Eliminar de la lista de doctores disponibles
        setAvailableDoctors(prev => prev.filter(d => d !== doctorName));
        
        // 2. Desasignar automáticamente de los consultorios
        setOffices(prevOffices => prevOffices.map(office => {
            if (office.medicoResponsable === doctorName) {
                return { ...office, medicoResponsable: '' }; // Deja el campo vacío o asigna un valor por defecto
            }
            return office;
        }));
    };


    // **********************************************
    // LÓGICA DE BÚSQUEDA Y FILTRADO
    // **********************************************
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Lista de consultorios filtrados
    const filteredOffices = offices.filter(office => {
        const term = searchTerm.toLowerCase();
        return (
            office.nombreConsultorio.toLowerCase().includes(term) ||
            office.piso.toLowerCase().includes(term) ||
            office.medicoResponsable.toLowerCase().includes(term) ||
            office.estado.toLowerCase().includes(term)
        );
    });

    // **********************************************
    // LÓGICA DE MANEJO DE FORMULARIO DE CONSULTORIOS
    // **********************************************

    const openModalForNew = () => {
        setFormData(DEFAULT_FORM_DATA);
        setIsEditing(false);
        setIsOfficeModalOpen(true);
    };

    const handleEdit = (office) => {
        setFormData(office);
        setIsEditing(true);
        setIsOfficeModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Maneja el envío del formulario (Crear o Editar)
    const handleSubmit = (e) => {
        e.preventDefault();
        
        const officeToSave = formData;

        if (isEditing) {
            // Actualizar consultorio
            setOffices(prevOffices => prevOffices.map(o => (o.id === officeToSave.id ? officeToSave : o)));
        } else {
            // Registrar nuevo consultorio
            const newOffice = { ...officeToSave, id: Date.now() }; // ID basado en timestamp
            setOffices(prevOffices => [...prevOffices, newOffice]);
        }

        // Limpiar y cerrar
        setFormData(DEFAULT_FORM_DATA);
        setIsOfficeModalOpen(false);
    };

    // **********************************************
    // LÓGICA DE ELIMINACIÓN CON MODAL
    // **********************************************
    
    // 1. Abre el modal de confirmación
    const handleDelete = (office) => {
        setOfficeToDelete(office);
        setIsConfirmModalOpen(true);
    };
    
    // 2. Ejecuta la eliminación
    const confirmDelete = () => {
        if (officeToDelete) {
            setOffices(prevOffices => prevOffices.filter(office => office.id !== officeToDelete.id));
        }
        setIsConfirmModalOpen(false);
        setOfficeToDelete(null);
    };


    // Función para determinar clases de color basadas en el estado
    const getStatusClasses = (status) => {
        switch (status) {
            case 'Activo':
                return 'bg-green-100 text-green-800';
            case 'En Mantenimiento':
                return 'bg-yellow-100 text-yellow-800';
            case 'Inactivo':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };


    return (
        <div className="p-4 sm:p-8 bg-gray-50 min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>
            <header className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
                <h1 className="text-3xl font-extrabold text-indigo-700 mb-4 md:mb-0 flex items-center">
                    {/* ICONO ACTUALIZADO: MapPin o Syringe para consultorios/clínica */}
                    <MapPin className="w-8 h-8 mr-3 text-indigo-500"/> Gestión de Consultorios
                </h1>
                <div className="flex space-x-3">
                    <button 
                        onClick={() => setIsDoctorModalOpen(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md transform hover:scale-[1.02] flex items-center"
                    >
                        {/* ICONO ACTUALIZADO: Syringe o UserCheck */}
                        <UserCheck className="w-5 h-5 mr-2"/> Gestionar Doctores
                    </button>
                    <button 
                        onClick={openModalForNew}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 shadow-md transform hover:scale-[1.02] flex items-center"
                    >
                        {/* ICONO ACTUALIZADO: Plus o SquarePlus */}
                        <SquarePlus className="w-5 h-5 mr-2" /> Nuevo Consultorio
                    </button>
                </div>
            </header>

            {/* BARRA DE BÚSQUEDA */}
            <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar consultorio por nombre, médico o estado..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition duration-150"
                />
            </div>
            
            {/* Tabla de Consultorios Registrados */}
            <div className="bg-white shadow-2xl rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-indigo-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Consultorio</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Piso</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Médico Responsable</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Estado</th>
                                <th className="px-6 py-3 text-center text-xs font-semibold text-indigo-700 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {filteredOffices.length > 0 ? (
                                filteredOffices.map((office) => (
                                    <tr key={office.id} className="hover:bg-indigo-50 transition duration-100">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{office.nombreConsultorio}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-semibold">{office.piso}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                            {office.medicoResponsable || 'Sin Asignar'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClasses(office.estado)}`}>
                                                {office.estado}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                                            <button 
                                                onClick={() => handleEdit(office)}
                                                className="text-green-600 hover:text-green-800 mr-4 p-2 rounded-lg hover:bg-green-100 transition duration-150"
                                                title="Editar"
                                            >
                                                {/* ICONO ACTUALIZADO: Pencil (Lápiz) */}
                                                <Pencil className="h-5 w-5" />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(office)}
                                                className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-100 transition duration-150"
                                                title="Eliminar"
                                            >
                                                {/* ICONO ACTUALIZADO: Trash2 (Basurero) */}
                                                <Trash2 className="h-5 w-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500 text-lg">
                                        No se encontraron consultorios que coincidan con la búsqueda.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ==================================================================== */}
            {/* MODAL PARA CREAR/EDITAR CONSULTORIO */}
            {/* ==================================================================== */}
            {isOfficeModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center backdrop-blur-sm">
                    <div className="bg-white p-8 rounded-xl shadow-3xl w-full max-w-md transform transition-all duration-300 scale-100">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-indigo-700 flex items-center">
                                {/* ICONOS ACTUALIZADOS EN EL TÍTULO DEL MODAL */}
                                {isEditing ? <Pencil className="w-6 h-6 mr-2" /> : <SquarePlus className="w-6 h-6 mr-2" />} 
                                {isEditing ? 'Editar Consultorio' : 'Registrar Nuevo Consultorio'}
                            </h2>
                            <button onClick={() => setIsOfficeModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {/* Campo Nombre Consultorio */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Nombre del Consultorio</label>
                                <input
                                    type="text"
                                    name="nombreConsultorio"
                                    value={formData.nombreConsultorio}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                    required
                                />
                            </div>
                            
                            {/* Selector de Piso */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Piso / Nivel</label>
                                <input
                                    type="number"
                                    name="piso"
                                    value={formData.piso}
                                    onChange={handleChange}
                                    min="1"
                                    max="99"
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                    required
                                />
                            </div>

                            {/* Selector de Médico Responsable (Usa la lista de estado) */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Médico Responsable</label>
                                <select
                                    name="medicoResponsable"
                                    value={formData.medicoResponsable}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                    required
                                >
                                    <option value="" disabled>Asignar Médico</option>
                                    {availableDoctors.map(doctor => (
                                        <option key={doctor} value={doctor}>{doctor}</option>
                                    ))}
                                    {/* Opción adicional si la lista está vacía */}
                                    {availableDoctors.length === 0 && (
                                        <option value="" disabled>No hay doctores registrados</option>
                                    )}
                                </select>
                            </div>
                            
                            {/* Selector de Estado */}
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Estado</label>
                                <select
                                    name="estado"
                                    value={formData.estado}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                    required
                                >
                                    {AVAILABLE_STATUSES.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3">
                                <button 
                                    type="button" 
                                    onClick={() => setIsOfficeModalOpen(false)}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-150 shadow-md flex items-center"
                                >
                                    <X className="w-5 h-5 mr-1" /> Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 shadow-md flex items-center"
                                >
                                    {/* ICONO ACTUALIZADO: Check */}
                                    <Check className="w-5 h-5 mr-1" /> {isEditing ? 'Guardar Cambios' : 'Registrar Consultorio'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* ==================================================================== */}
            {/* MODAL DE GESTIÓN DE DOCTORES */}
            {/* ==================================================================== */}
            {isDoctorModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center backdrop-blur-sm">
                    <div className="bg-white p-8 rounded-xl shadow-3xl w-full max-w-md transform transition-all duration-300 scale-100">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-purple-700 flex items-center">
                                {/* ICONO ACTUALIZADO: UserCheck */}
                                <UserCheck className="w-6 h-6 mr-2" /> Gestionar Médicos Disponibles
                            </h2>
                            <button onClick={() => setIsDoctorModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Formulario para agregar nuevo doctor */}
                        <form onSubmit={handleAddDoctor} className="flex space-x-2 mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <input
                                type="text"
                                value={newDoctorName}
                                onChange={(e) => setNewDoctorName(e.target.value)}
                                placeholder="Nombre completo del Dr. o Dra."
                                className="flex-grow border border-gray-300 rounded-lg py-2 px-3 text-gray-800 focus:ring-purple-500 focus:border-purple-500"
                                required
                            />
                            <button 
                                type="submit"
                                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 shadow-md flex items-center"
                            >
                                {/* ICONO ACTUALIZADO: Plus */}
                                <Plus className="w-5 h-5 mr-1" /> Agregar
                            </button>
                        </form>
                        
                        {/* Lista de Doctores Actuales */}
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                            {availableDoctors.length > 0 ? (
                                availableDoctors.map((doctor, index) => (
                                    <div key={index} className="flex justify-between items-center bg-white border border-gray-200 p-3 rounded-lg shadow-sm">
                                        <span className="text-gray-800 font-medium flex items-center">
                                            <UserCheck className="w-4 h-4 mr-2 text-purple-500" /> {doctor}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteDoctor(doctor)}
                                            className="text-red-500 hover:text-red-700 p-1 rounded-full transition duration-150"
                                            title="Eliminar Doctor y desasignar de consultorios"
                                        >
                                            {/* ICONO ACTUALIZADO: UserMinus (O Trash2) */}
                                            <UserMinus className="h-5 w-5" />
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500">Aún no hay doctores registrados.</p>
                            )}
                        </div>

                        {/* Botón cerrar */}
                        <div className="mt-6 flex justify-end">
                            <button 
                                type="button" 
                                onClick={() => setIsDoctorModalOpen(false)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 shadow-md flex items-center"
                            >
                                <X className="w-5 h-5 mr-1" /> Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* ==================================================================== */}
            {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN */}
            {/* ==================================================================== */}
            {isConfirmModalOpen && officeToDelete && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center backdrop-blur-sm">
                    <div className="bg-white p-6 rounded-xl shadow-3xl w-full max-w-sm transform transition-all duration-300">
                        <div className="text-center">
                            {/* ICONO ACTUALIZADO: AlertTriangle (Advertencia) */}
                            <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
                            <h3 className="text-lg font-bold leading-6 text-gray-900 mt-4">Confirmar Eliminación</h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    ¿Estás seguro de que deseas eliminar el consultorio 
                                    <span className="font-semibold text-red-700"> "{officeToDelete.nombreConsultorio}"</span>? Esta acción no se puede deshacer.
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-6 flex justify-between space-x-3">
                            <button
                                type="button"
                                className="inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm transition duration-150 flex items-center"
                                onClick={() => { setIsConfirmModalOpen(false); setOfficeToDelete(null); }}
                            >
                                <X className="w-5 h-5 mr-1" /> Cancelar
                            </button>
                            <button
                                type="button"
                                className="inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:text-sm transition duration-150 flex items-center"
                                onClick={confirmDelete}
                            >
                                <Trash2 className="w-5 h-5 mr-1" /> Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Consultorios;